import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  chatExpedienteStream,
  type HerramientaEvento,
  type IntencionDetectada,
} from "@/lib/api";
import type { ExpedienteAnalizado, MensajeChat } from "@/types";
import { useAuth } from "@/hooks/useAuth";
import { formatearFechaCorta } from "@/lib/utils";

interface Props {
  expediente: ExpedienteAnalizado;
  // Cuando el padre quiere prellenar el input (ej. click en una fila
  // de "Datos pendientes" desde ExpedienteDetail), pasa el texto aquí.
  prefill?: string;
  onPrefillUsado?: () => void;
  onClickPendiente?: (label: string) => void;
}

const SUGERENCIAS_INICIALES = [
  "¿Por qué decidiste así?",
  "¿Qué dice el Art 149?",
  "Reformula el considerando 2",
  "/ayuda",
];

const BADGE_INTENCION: Record<IntencionDetectada["tipo"], { label: string; cls: string }> = {
  CONSULTAR: { label: "❓ Consulta", cls: "bg-slate-200 text-slate-700" },
  APORTAR_INFO: { label: "✓ Información aportada", cls: "bg-emerald-200 text-emerald-900" },
  CAMBIAR_DECISION: { label: "✏️ Cambio de decisión", cls: "bg-amber-200 text-amber-900" },
  REFORMULAR_CARTA: { label: "✏️ Carta reformulada", cls: "bg-sky-200 text-sky-900" },
  APROBAR: { label: "✓ Aprobado", cls: "bg-emerald-300 text-emerald-900" },
  RECHAZAR: { label: "✗ Rechazado", cls: "bg-rose-200 text-rose-900" },
  AYUDA: { label: "ℹ️ Ayuda", cls: "bg-blue-200 text-blue-900" },
};

interface SlashCommand {
  comando: string;
  descripcion: string;
  prefill: string;
}

const SLASH_COMMANDS: SlashCommand[] = [
  { comando: "/ayuda", descripcion: "Ver lista de comandos y ejemplos", prefill: "/ayuda" },
  {
    comando: "/aportar",
    descripcion: "Aportar información (lectura, OS, fecha, etc.)",
    prefill: "Aporta la lectura del medidor: ___ kWh con fecha ___",
  },
  {
    comando: "/reformular",
    descripcion: "Reformular una sección de la carta",
    prefill: "Reformula el considerando ___ con tono ___",
  },
  {
    comando: "/cambiar",
    descripcion: "Cambiar la decisión del motor",
    prefill: "Cambia a ___ porque ___",
  },
  {
    comando: "/aprobar",
    descripcion: "Aprobar el expediente (rol supervisor)",
    prefill: "Aprueba el expediente",
  },
  {
    comando: "/rechazar",
    descripcion: "Rechazar el expediente (rol supervisor)",
    prefill: "Rechaza, motivo: ___",
  },
];

export function ChatPanel({
  expediente,
  prefill,
  onPrefillUsado,
}: Props) {
  const { sesion } = useAuth();
  const queryClient = useQueryClient();
  const [mensaje, setMensaje] = useState("");
  const [ultimaIntencion, setUltimaIntencion] = useState<IntencionDetectada | null>(null);
  const [sugerencias, setSugerencias] = useState<string[]>([]);
  const [ultimoEnviado, setUltimoEnviado] = useState<string | null>(null);
  const [streamingTexto, setStreamingTexto] = useState<string>("");
  const [streamingActivo, setStreamingActivo] = useState(false);
  const [herramientas, setHerramientas] = useState<ChipHerramienta[]>([]);
  const [errorStream, setErrorStream] = useState<string | null>(null);
  const refScroll = useRef<HTMLDivElement | null>(null);
  const refInput = useRef<HTMLTextAreaElement | null>(null);

  const slashAbierto = mensaje.startsWith("/") && !mensaje.includes(" ");
  const slashFiltrados = useMemo(
    () =>
      slashAbierto
        ? SLASH_COMMANDS.filter((c) =>
            c.comando.toLowerCase().startsWith(mensaje.toLowerCase())
          )
        : [],
    [mensaje, slashAbierto]
  );

  useEffect(() => {
    if (refScroll.current) {
      refScroll.current.scrollTop = refScroll.current.scrollHeight;
    }
  }, [expediente.chat.length, streamingActivo, streamingTexto]);

  // Cuando el padre nos pasa un prefill (click en una fila de "Datos
  // pendientes" desde el card del expediente), lo cargamos al input y
  // enfocamos al final del texto para que el operador empiece a escribir.
  useEffect(() => {
    if (prefill) {
      setMensaje(prefill);
      onPrefillUsado?.();
      // Defer al siguiente tick para asegurar que el textarea está montado.
      setTimeout(() => {
        const el = refInput.current;
        if (el) {
          el.focus();
          el.setSelectionRange(prefill.length, prefill.length);
        }
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  function manejarEnvio(e: FormEvent) {
    e.preventDefault();
    enviar();
  }

  async function enviar(textoOverride?: string) {
    const texto = (textoOverride ?? mensaje).trim();
    if (!texto || streamingActivo) return;
    setUltimoEnviado(texto);
    setStreamingTexto("");
    setStreamingActivo(true);
    setErrorStream(null);
    setMensaje("");
    setSugerencias([]);
    setHerramientas([]);
    let textoAcumulado = "";
    try {
      await chatExpedienteStream(
        expediente.expediente_id,
        texto,
        sesion?.sub ?? "anon",
        {
          onIntencion: (intencion) => {
            setUltimaIntencion(intencion);
          },
          onChunk: (chunk) => {
            textoAcumulado += chunk;
            setStreamingTexto(textoAcumulado);
          },
          onHerramienta: (h) => {
            setHerramientas((prev) => aplicarEventoHerramienta(prev, h));
          },
          onDone: (data) => {
            queryClient.setQueryData(
              ["expediente", data.expediente.expediente_id],
              data.expediente,
            );
            queryClient.invalidateQueries({ queryKey: ["expedientes"] });
            setSugerencias(data.sugerencias ?? []);
            setStreamingActivo(false);
            setStreamingTexto("");
            requestAnimationFrame(() => refInput.current?.focus());
          },
          onError: (msg) => {
            setErrorStream(msg);
            setStreamingActivo(false);
          },
        },
      );
    } catch (exc) {
      setErrorStream(String(exc));
      setStreamingActivo(false);
    }
  }

  function manejarTeclas(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      enviar();
      return;
    }
    if (slashAbierto && slashFiltrados.length > 0 && e.key === "Tab") {
      e.preventDefault();
      const primero = slashFiltrados[0];
      setMensaje(primero.prefill);
      return;
    }
  }

  function usarSugerencia(texto: string) {
    setMensaje(texto);
    requestAnimationFrame(() => refInput.current?.focus());
  }

  function reintentar() {
    if (ultimoEnviado) {
      enviar(ultimoEnviado);
    }
  }

  return (
    <div className="card flex flex-col h-full min-h-0">
      <div className="border-b border-slate-200 px-4 py-2 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <span>🤖</span> Chat con GEMA
        </h2>
        <span className="text-xs text-slate-500">
          {expediente.chat.length} mensaje{expediente.chat.length === 1 ? "" : "s"}
        </span>
      </div>

      {/* Banner de pendientes — sticky al tope del scroll, siempre
          visible cuando hay placeholders pendientes. */}
      <BannerPendientesChat
        expediente={expediente}
        onClickPendiente={usarSugerencia}
      />

      <div ref={refScroll} className="flex-1 overflow-auto p-4 space-y-3 min-h-[300px]">
        {expediente.chat.length === 0 && (
          <ChatVacio expediente={expediente} onSugerencia={usarSugerencia} />
        )}
        {expediente.chat.map((m) => (
          <Mensaje key={m.id} mensaje={m} />
        ))}
        {herramientas.length > 0 && <ChipsHerramienta items={herramientas} />}
        {streamingActivo && streamingTexto === "" && herramientas.length === 0 && (
          <Escribiendo />
        )}
        {streamingActivo && streamingTexto !== "" && (
          <MensajeStreaming texto={streamingTexto} />
        )}
        {ultimaIntencion && !streamingActivo && (
          <BadgeIntencion intencion={ultimaIntencion} />
        )}
        {!streamingActivo && sugerencias.length > 0 && (
          <Sugerencias items={sugerencias} onClick={usarSugerencia} />
        )}
      </div>

      <form onSubmit={manejarEnvio} className="border-t border-slate-200 p-3 relative">
        {slashAbierto && slashFiltrados.length > 0 && (
          <SlashPopup
            items={slashFiltrados}
            onSeleccionar={(c) => {
              setMensaje(c.prefill);
              requestAnimationFrame(() => refInput.current?.focus());
            }}
          />
        )}
        <div className="flex gap-2 items-end">
          <textarea
            ref={refInput}
            value={mensaje}
            onChange={(e) => setMensaje(e.target.value)}
            onKeyDown={manejarTeclas}
            placeholder="Escribe tu mensaje (Enter envía, Shift+Enter salto de línea). Comandos con /"
            rows={Math.min(Math.max(mensaje.split("\n").length, 1), 5)}
            className="input flex-1 resize-none"
            disabled={streamingActivo}
          />
          <button
            type="submit"
            className="btn-primary"
            disabled={streamingActivo || !mensaje.trim()}
            aria-label="Enviar"
          >
            {streamingActivo ? "…" : "Enviar"}
          </button>
        </div>
        <p className="text-[10px] text-slate-400 mt-1">
          <kbd className="px-1 rounded bg-slate-100">Enter</kbd> envía ·
          <kbd className="px-1 rounded bg-slate-100">Shift+Enter</kbd> nueva línea ·
          escribe <code>/</code> para ver comandos.
        </p>
      </form>

      {errorStream && (
        <div className="px-3 pb-3 flex items-center gap-2 text-xs text-rose-600">
          <span>Error: {errorStream}</span>
          <button
            type="button"
            onClick={reintentar}
            className="text-rose-700 underline hover:no-underline"
          >
            Reintentar
          </button>
        </div>
      )}
    </div>
  );
}

function MensajeStreaming({ texto }: { texto: string }) {
  return (
    <div className="flex justify-start items-end gap-2">
      <Avatar tipo="gema" />
      <div className="bg-slate-100 text-slate-800 rounded-lg px-3 py-2 max-w-[85%]">
        <div className="text-sm chat-md">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{texto}</ReactMarkdown>
        </div>
        <span className="inline-block w-1.5 h-3 bg-slate-500 ml-0.5 align-text-bottom animate-pulse rounded-sm" />
      </div>
    </div>
  );
}

function SlashPopup({
  items,
  onSeleccionar,
}: {
  items: SlashCommand[];
  onSeleccionar: (c: SlashCommand) => void;
}) {
  return (
    <div className="absolute bottom-full left-3 right-3 mb-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-56 overflow-auto z-10">
      <ul className="divide-y divide-slate-100">
        {items.map((c) => (
          <li key={c.comando}>
            <button
              type="button"
              onClick={() => onSeleccionar(c)}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2"
            >
              <code className="text-xs font-mono text-slate-900 bg-slate-100 px-1.5 py-0.5 rounded">
                {c.comando}
              </code>
              <span className="text-xs text-slate-600 flex-1">{c.descripcion}</span>
            </button>
          </li>
        ))}
      </ul>
      <p className="text-[10px] text-slate-400 px-3 py-1 border-t border-slate-100">
        Tab para autocompletar
      </p>
    </div>
  );
}

function ChatVacio({
  expediente,
  onSugerencia,
}: {
  expediente: ExpedienteAnalizado;
  onSugerencia: (s: string) => void;
}) {
  // Pendientes que el operador puede aportar conversacionalmente.
  const aportados = expediente.placeholders_carta ?? {};
  const pendientes = (expediente.placeholders_pendientes ?? []).filter(
    (p) => !aportados[p],
  );
  const tienePendientes = pendientes.length > 0;

  return (
    <div className="mt-4 space-y-4">
      <div className="text-center">
        <div className="text-3xl mb-2">🤖</div>
        <p className="text-sm font-medium text-slate-700">Hola, soy GEMA</p>
        <p className="text-xs text-slate-500 max-w-sm mx-auto mt-1">
          Pregúntame por qué decidí así, aporta información del caso, pide
          cambios a la carta o aprueba el expediente. Detecto tu intención
          automáticamente.
        </p>
      </div>

      {/* Pendientes en el chat (cuando los hay) — clic en cada uno
          prefill del input para que el operador escriba el valor. */}
      {tienePendientes && (
        <div
          style={{
            border: "1px solid oklch(0.88 0.07 85)",
            background: "var(--amber-soft)",
            borderRadius: "var(--r-md)",
            padding: "12px 14px",
          }}
        >
          <div
            className="eyebrow"
            style={{ marginBottom: 8, color: "var(--amber-deep)" }}
          >
            📝 Datos pendientes · {pendientes.length}
          </div>
          <p
            style={{
              margin: "0 0 10px",
              fontSize: 12.5,
              color: "var(--ink-2)",
              lineHeight: 1.5,
            }}
          >
            Pulsa un dato para empezar a escribirlo. GEMA lo detecta y lo
            agrega al expediente.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {pendientes.map((p) => {
              const label = labelPendiente(p);
              return (
                <button
                  key={p}
                  onClick={() => onSugerencia(`Aportar ${label}: `)}
                  style={{
                    fontSize: 11.5,
                    padding: "4px 10px",
                    borderRadius: 999,
                    border: "1px solid oklch(0.85 0.10 80)",
                    background: "white",
                    color: "var(--amber-deep)",
                    fontFamily: "inherit",
                    cursor: "pointer",
                  }}
                  title={p}
                >
                  + {label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <div
          className="eyebrow"
          style={{ marginBottom: 8, textAlign: "center" }}
        >
          O empieza preguntando
        </div>
        <div className="flex flex-wrap gap-2 justify-center">
          {SUGERENCIAS_INICIALES.map((s) => (
            <button
              key={s}
              onClick={() => onSugerencia(s)}
              className="text-xs px-2 py-1 rounded-full border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-700"
            >
              {s}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Conversión rápida del placeholder técnico a un label legible que el
// operador entiende al ver el chip en el chat. Espejo simplificado de
// `describirPlaceholder` que vive en ExpedienteDetail.
function labelPendiente(p: string): string {
  const map: Record<string, string> = {
    fecha_desocupacion: "fecha de desocupación",
    soporte_desocupacion: "soporte de desocupación",
    correo_electronico: "correo electrónico",
    nombre_usuario: "nombre del usuario",
    nombre_cliente: "nombre del cliente",
    numero_decision_anterior: "número de decisión anterior",
    fecha_decision_anterior: "fecha de decisión anterior",
    firmante_decision_anterior: "firmante decisión anterior",
    respuesta_peticiones_usuario: "respuesta a peticiones",
    lectura_factura: "lectura de la factura",
    lectura_anterior: "lectura anterior",
  };
  if (map[p]) return map[p];
  if (/^valor_mes_\d$/.test(p)) return `valor mes ${p.slice(-1)}`;
  if (/^consumo_mes_\d$/.test(p)) return `consumo mes ${p.slice(-1)}`;
  if (/^mes_\d$/.test(p)) return `etiqueta mes ${p.slice(-1)}`;
  return p.replace(/_/g, " ");
}

function Escribiendo() {
  return (
    <div className="flex justify-start items-end gap-2">
      <Avatar tipo="gema" />
      <div className="bg-slate-100 text-slate-800 rounded-lg px-3 py-2 inline-flex items-center gap-1">
        <span className="text-xs text-slate-500">GEMA escribiendo</span>
        <span className="flex gap-0.5 ml-1">
          <span className="animate-pulse">●</span>
          <span className="animate-pulse" style={{ animationDelay: "0.15s" }}>●</span>
          <span className="animate-pulse" style={{ animationDelay: "0.3s" }}>●</span>
        </span>
      </div>
    </div>
  );
}

type ChipHerramienta = { nombre: string; estado: "activo" | "ok" | "error" };

const LABEL_HERRAMIENTA: Record<string, string> = {
  aportar_informacion: "aplicando la información",
  solicitar_cambio_decision: "registrando el cambio de decisión",
  reformular_seccion_carta: "reformulando la carta",
  regenerar_carta_completa: "regenerando la carta",
  reprocesar: "reprocesando con el motor",
  registrar_os: "registrando la orden de servicio",
  aprobar: "aprobando el expediente",
  rechazar: "rechazando el expediente",
};

function aplicarEventoHerramienta(
  prev: ChipHerramienta[],
  h: HerramientaEvento,
): ChipHerramienta[] {
  if (h.fase === "inicio") {
    return [...prev, { nombre: h.nombre, estado: "activo" }];
  }
  const copia = [...prev];
  for (let i = copia.length - 1; i >= 0; i--) {
    if (copia[i].nombre === h.nombre && copia[i].estado === "activo") {
      copia[i] = { nombre: h.nombre, estado: h.ok ? "ok" : "error" };
      break;
    }
  }
  return copia;
}

function ChipsHerramienta({ items }: { items: ChipHerramienta[] }) {
  const base =
    "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium";
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((h, i) => {
        const label = LABEL_HERRAMIENTA[h.nombre] ?? h.nombre;
        if (h.estado === "activo") {
          return (
            <span key={i} className={`${base} bg-sky-50 text-sky-700`}>
              <span className="h-2 w-2 rounded-full bg-sky-500 animate-pulse" />
              GEMA está {label}…
            </span>
          );
        }
        if (h.estado === "error") {
          return (
            <span key={i} className={`${base} bg-rose-50 text-rose-700`}>
              ✕ {label} — no aplicada
            </span>
          );
        }
        return (
          <span key={i} className={`${base} bg-emerald-50 text-emerald-700`}>
            ✓ {label}
          </span>
        );
      })}
    </div>
  );
}

function BadgeIntencion({ intencion }: { intencion: IntencionDetectada }) {
  const cfg = BADGE_INTENCION[intencion.tipo];
  if (!cfg) return null;
  return (
    <div className="flex justify-end">
      <span
        className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${cfg.cls}`}
        title={`${intencion.razon} · confianza ${(intencion.confianza * 100).toFixed(0)}%`}
      >
        {cfg.label}
      </span>
    </div>
  );
}

function Sugerencias({
  items,
  onClick,
}: {
  items: string[];
  onClick: (s: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2 pt-1">
      <span className="text-[10px] text-slate-400 self-center mr-1">Sugerencias:</span>
      {items.map((s, i) => (
        <button
          key={i}
          onClick={() => onClick(s)}
          className="text-xs px-2 py-1 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 transition"
        >
          {s}
        </button>
      ))}
    </div>
  );
}

function Avatar({ tipo }: { tipo: "usuario" | "gema" }) {
  if (tipo === "gema") {
    return (
      <span
        className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gema-100 text-gema-700 text-sm shrink-0"
        aria-label="GEMA"
        title="GEMA"
      >
        🤖
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-200 text-slate-700 text-sm shrink-0"
      aria-label="Tú"
      title="Tú"
    >
      👤
    </span>
  );
}

function Mensaje({ mensaje }: { mensaje: MensajeChat }) {
  const esGema = mensaje.rol === "gema";
  return (
    <div className={`flex ${esGema ? "justify-start" : "justify-end"} items-end gap-2`}>
      {esGema && <Avatar tipo="gema" />}
      <div
        className={
          esGema
            ? "bg-slate-100 text-slate-800 rounded-lg px-3 py-2 max-w-[85%]"
            : "bg-gema-600 text-white rounded-lg px-3 py-2 max-w-[80%]"
        }
      >
        {esGema ? (
          <div className="text-sm chat-md">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {mensaje.contenido}
            </ReactMarkdown>
          </div>
        ) : (
          <p className="text-sm whitespace-pre-wrap">{mensaje.contenido}</p>
        )}
        <p
          className={
            esGema
              ? "text-[10px] text-slate-500 mt-1"
              : "text-[10px] text-gema-100 mt-1"
          }
        >
          {formatearFechaCorta(mensaje.timestamp)}
        </p>
      </div>
      {!esGema && <Avatar tipo="usuario" />}
    </div>
  );
}

// Banner sticky en el chat con los datos pendientes — chips clickeables
// que prefillan el input para que el operador aporte conversacionalmente.
function BannerPendientesChat({
  expediente,
  onClickPendiente,
}: {
  expediente: ExpedienteAnalizado;
  onClickPendiente: (texto: string) => void;
}) {
  const aportados = expediente.placeholders_carta ?? {};
  const pendientes = (expediente.placeholders_pendientes ?? []).filter(
    (p) => !aportados[p],
  );
  if (pendientes.length === 0) return null;
  return (
    <div
      style={{
        padding: "10px 14px",
        background: "var(--amber-soft)",
        borderBottom: "1px solid oklch(0.88 0.07 85)",
      }}
    >
      <div
        style={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: "var(--amber-deep)",
          fontWeight: 500,
          marginBottom: 6,
        }}
      >
        📝 Datos pendientes · {pendientes.length}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {pendientes.map((p) => {
          const label = labelPendiente(p);
          return (
            <button
              key={p}
              onClick={() => onClickPendiente(`Aportar ${label}: `)}
              title={p}
              style={{
                fontSize: 11.5,
                padding: "4px 10px",
                borderRadius: 999,
                border: "1px solid oklch(0.85 0.10 80)",
                background: "white",
                color: "var(--amber-deep)",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              + {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
